import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { noteBusy, noteErrorToast, backdropBusy, BUSY_SELECTOR } from './loadSignal';
import { confirmCopy } from './confirmCopy';
import { markRefusedRow, clearFixed } from '../grid/rowValidation';

const toastsSeen = new WeakSet();

// New UI only: one way to leave every popup, and buttons that read the same everywhere
// (docs/ui_redesign/18-ux-review.md, R1, R2, R3, R7 and R13). It works on the dialogs Classic already renders:
// - Escape clicks the top dialog's own Cancel / No / Close, so the same Classic handler runs.
// - A ✕ in the corner does the same.
// - In the button row, the dismiss button goes first (left) and quiet, the action last and filled, red when it
//   destroys or discards something.
// - A confirm that destroys something starts with focus on Cancel; a lookup starts in its search box.
// - A dialog without an accessible name takes its visible title.
// - A side panel holding a short list (three columns or fewer) opens narrower.
// Full-screen dialogs (the invoice editor, Vendor Mapping, Invoice Model Setup) keep their own header and keys.

const text = el => (el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '');
const shown = el => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
const labelOf = b => (text(b) || b.getAttribute('aria-label') || '').toLowerCase();

export const DISMISS_LABELS = ['cancel', 'no', 'keep editing', 'close'];
const DESTRUCTIVE = /^(delete|remove|discard|reset|move invoice|change vendor|clear)\b/i;
const DESTRUCTIVE_CONTEXT = /\b(delete|remove|discard|unsaved|lose|reset)\b/i;

const inGrid = b => !!b.closest('.MuiDataGrid-root');
const ownX = b => !!b.closest('.apw2-dk-host');

// Buttons of the dialog itself, not of a grid or a form control inside it.
function dialogButtons(paper, enabledOnly) {
	return [...paper.querySelectorAll('button, [role="button"]')].filter(b => !inGrid(b) && !ownX(b) && (!enabledOnly || !b.disabled) && shown(b)
		&& !b.closest('.MuiInputBase-root, .MuiTabs-root, .MuiToggleButtonGroup-root, .MuiAccordionSummary-root'));
}

// The button that leaves without doing anything: a screen can mark it (data-apw2-dismiss); otherwise Cancel, No,
// Keep editing, then Close. A lookup marks its Close as not a dismiss while a row is ticked (it applies the row).
export function dismissButton(paper) {
	if (!paper) return null;
	const buttons = dialogButtons(paper, true);
	const marked = buttons.find(b => b.dataset.apw2Dismiss === 'true');
	if (marked) return marked;
	for (const want of DISMISS_LABELS) { // by today's label, or by Classic's when R2 renamed it ("Keep IO/POs" was "No")
		const hit = buttons.find(b => (labelOf(b) === want || classicText(b).toLowerCase() === want) && b.dataset.apw2Dismiss !== 'false');
		if (hit) return hit;
	}
	return null;
}

// Roles for the button row: dismiss, primary (the filled one, else the last), secondary, and a danger tone.
// Some panels put Save / Cancel straight under the grid instead of in a button row (the mapping panels). Their parent
// becomes the row: the popup helper marks it and CSS lays the buttons out at the bottom right.
const LOOSE = /^(save|cancel|close|ok|submit|select|apply)$/i;
export function looseActions(paper) {
	const buttons = dialogButtons(paper).filter(b => LOOSE.test(text(b)) && b.parentElement && !b.parentElement.closest('.MuiDialogActions-root'));
	if (!buttons.length) return null;
	const parent = buttons[buttons.length - 1].parentElement;
	const mine = dialogButtons(paper).filter(b => b.parentElement === parent); // every button beside them, whatever its label
	return [...parent.children].some(c => !mine.includes(c)) ? { parent, buttons: mine } : null;
}

export function buttonRoles(paper) {
	const rows = paper.querySelectorAll('.MuiDialogActions-root');
	let row = rows[rows.length - 1];
	let buttons;
	if (row) buttons = dialogButtons(row);
	else {
		const loose = looseActions(paper);
		if (!loose) return null;
		if (loose.parent.dataset.apw2LooseActions !== 'true') loose.parent.dataset.apw2LooseActions = 'true';
		row = loose.parent;
		buttons = loose.buttons;
	}
	if (!buttons.length) return null;
	const dismiss = buttons.includes(dismissButton(row)) ? dismissButton(row) : null;
	const rest = buttons.filter(b => b !== dismiss && labelOf(b));
	const primary = rest.find(b => b.classList.contains('MuiButton-contained')) || rest[rest.length - 1] || null;
	const context = text(paper.querySelector('.MuiDialogTitle-root')) + ' ' + text(paper.querySelector('.MuiDialogContent-root'));
	const label = primary ? labelOf(primary) : '';
	const danger = !!primary && (primary.dataset.apw2Tone === 'danger' || DESTRUCTIVE.test(label)
		|| (/^(yes|ok)$/.test(label) && DESTRUCTIVE_CONTEXT.test(context)));
	return { dismiss, primary, secondary: rest.filter(b => b !== primary), danger };
}

export function isOpenModal(root) {
	if (!root || !root.classList || !root.classList.contains('MuiModal-root')) return false;
	const cs = window.getComputedStyle(root);
	if (cs.visibility === 'hidden' || cs.display === 'none') return false;
	const container = root.querySelector('.MuiDialog-container, .MuiPopover-paper, .MuiMenu-paper');
	return !(container && container.style && container.style.opacity === '0');
}

// The top open popup (dialogs, menus and popovers are all MUI modals rendered under <body>, newest last).
export function topModal(doc = document) {
	const roots = [...doc.body.children].filter(isOpenModal);
	return roots[roots.length - 1] || null;
}

const paperOf = root => root && root.querySelector('.MuiDialog-paper');
const isFullScreen = paper => !!paper && (paper.classList.contains('MuiDialog-paperFullScreen') || !!paper.querySelector(':scope > .apw2-ed, :scope > .apw2-vm'));
const isPanel = paper => !!paper && (paper.classList.contains('MuiDialog-paperWidthLg') || paper.classList.contains('MuiDialog-paperWidthXl')) && !isFullScreen(paper);
const hasOwnClose = paper => [...paper.querySelectorAll('.MuiDialogTitle-root button, button[aria-label="close" i], button[aria-label="Close dialog" i]')].some(b => !ownX(b));

export function titleText(paper) {
	const t = paper.querySelector('.MuiDialogTitle-root, h1, h2, h3, h4, h5, h6, .MuiTypography-h6, .MuiTypography-h5');
	return text(t).slice(0, 120);
}

// A short list in a side panel (a code and a name, or one mapping column) doesn't need 1,200px.
export function shortList(paper) {
	const grid = paper.querySelector('.MuiDataGrid-root');
	if (!grid) return false;
	const cols = [...grid.querySelectorAll('.MuiDataGrid-columnHeader')].filter(h => !h.classList.contains('MuiDataGrid-columnHeaderCheckbox')
		&& !/^(actions?|__check__|__detail_panel_toggle__)$/i.test(h.getAttribute('data-field') || ''));
	return cols.length > 0 && cols.length <= 3 && !paper.querySelector('.MuiDialogContent-root form, .row-form');
}

// R2: the new wording is written into Classic's own text nodes. Each node remembers the text Classic gave it, so a
// rule always matches Classic's words, and a node React changes later is read again rather than overwritten blindly.
const memo = new WeakMap();
function textNodes(el) {
	const out = [];
	const walk = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
	for (let n = walk.nextNode(); n; n = walk.nextNode()) if (!n.parentElement.closest('svg, .apw2-dk-host')) out.push(n);
	return out;
}
export function classicText(el) {
	if (!el) return '';
	return textNodes(el).map(n => {
		const m = memo.get(n);
		if (m && n.nodeValue === m.mine) return m.orig;
		if (m) memo.delete(n);
		return n.nodeValue;
	}).join('').replace(/\s+/g, ' ').trim();
}
export function writeText(el, value) {
	const nodes = textNodes(el);
	if (!nodes.length) {
		const n = el.ownerDocument.createTextNode(value);
		memo.set(n, { orig: '', mine: value });
		el.appendChild(n);
		return;
	}
	nodes.forEach((n, i) => {
		const m = memo.get(n);
		const orig = m && n.nodeValue === m.mine ? m.orig : n.nodeValue;
		const mine = i === 0 ? value : '';
		if (n.nodeValue !== mine) n.nodeValue = mine;
		memo.set(n, { orig, mine });
	});
}

const YES = /^(yes|ok|yes, clear all)$/i;
const NO = /^(no|cancel)$/i;
export function applyCopy(paper) {
	const rows = paper.querySelectorAll('.MuiDialogActions-root');
	const row = rows[rows.length - 1];
	const content = paper.querySelector('.MuiDialogContent-root');
	const plain = content && !content.querySelector('input, select, textarea, button, table, .MuiDataGrid-root');
	const msg = paper.querySelector('.MuiDialogContentText-root') || (plain ? content.querySelector('.MuiTypography-root') || content : null);
	if (!row || !msg) return null;
	const buttons = dialogButtons(row);
	const yes = buttons.find(b => YES.test(classicText(b)));
	const no = buttons.find(b => NO.test(classicText(b)));
	let title = paper.querySelector('.MuiDialogTitle-root');
	const copy = confirmCopy({ title: classicText(title), message: classicText(msg), confirm: yes ? classicText(yes) : '', dismiss: no ? classicText(no) : '' });
	if (!copy) return null;
	if (copy.title) {
		if (!title) { // a confirm without a heading (GenericMessageDialog) gets one
			title = paper.ownerDocument.createElement('h2');
			title.className = 'MuiTypography-root MuiTypography-h6 MuiDialogTitle-root apw2-dk-title';
			paper.insertBefore(title, paper.querySelector(':scope > .MuiDialogContent-root') || null);
		}
		if (text(title) !== copy.title) writeText(title, copy.title);
	}
	if (copy.body != null && text(msg) !== copy.body) writeText(msg, copy.body);
	if (copy.confirm && yes && text(yes) !== copy.confirm) writeText(yes, copy.confirm);
	if (copy.dismiss && no && text(no) !== copy.dismiss) writeText(no, copy.dismiss);
	if (copy.rename) buttons.forEach(b => { const to = copy.rename[classicText(b)]; if (to && text(b) !== to) writeText(b, to); });
	if (copy.danger && yes) yes.dataset.apw2Tone = 'danger';
	return copy;
}

const seen = new WeakSet();
function tidy(root) {
	const paper = paperOf(root);
	if (!paper || isFullScreen(paper)) return null;
	applyCopy(paper);
	const roles = buttonRoles(paper);
	if (roles) {
		const set = (b, role) => { if (b && b.dataset.apw2Role !== role) b.dataset.apw2Role = role; };
		set(roles.dismiss, 'dismiss');
		set(roles.primary, 'primary');
		roles.secondary.forEach(b => set(b, 'secondary'));
		if (roles.primary) {
			if (roles.danger) roles.primary.dataset.apw2Tone = 'danger';
			else if (roles.primary.dataset.apw2Tone === 'danger' && !DESTRUCTIVE.test(labelOf(roles.primary))) delete roles.primary.dataset.apw2Tone;
		}
	}
	const dialog = paper.getAttribute('role') === 'dialog' ? paper : paper.querySelector('[role="dialog"]') || paper;
	const by = (dialog.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
	const named = (dialog.getAttribute('aria-label') || '').trim() || by.some(id => text(document.getElementById(id)));
	if (!named) { const t = titleText(paper); if (t) dialog.setAttribute('aria-label', t); }
	if (isPanel(paper)) {
		const narrow = shortList(paper);
		if (narrow && paper.dataset.apw2Panel !== 'narrow') paper.dataset.apw2Panel = 'narrow';
		else if (!narrow && paper.dataset.apw2Panel === 'narrow') delete paper.dataset.apw2Panel;
	}
	if (!seen.has(paper) && roles && (roles.dismiss || roles.primary)) {
		seen.add(paper);
		const active = document.activeElement;
		const typing = active && paper.contains(active) && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName);
		const search = (paper.dataset.apw2Lookup || paper.querySelector('.MuiDataGrid-columnHeaderCheckbox'))
			&& paper.querySelector('input[name="data-grid-quick-filter"], input[placeholder^="Search" i], input[type="search"]');
		const stillIdle = () => paper.isConnected && !(paper.contains(document.activeElement) && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName));
		if (!typing && search) setTimeout(() => { if (stillIdle()) search.focus(); }, 60);
		else if (!typing && roles.danger && roles.dismiss) setTimeout(() => { if (stillIdle()) roles.dismiss.focus(); }, 60);
	}
	const dismiss = dismissButton(paper);
	if (!dismiss || hasOwnClose(paper)) return null;
	let host = paper.querySelector(':scope > .apw2-dk-host');
	if (!host) {
		host = document.createElement('div');
		host.className = 'apw2-dk-host';
		paper.insertBefore(host, paper.firstChild);
	}
	return { host, paper };
}

// R3: in a single-choice lookup, clicking anywhere on a row ticks it (the row's own checkbox, so the lookup's handler runs).
export function onLookupRowClick(e) {
	const row = e.target.closest && e.target.closest('[data-apw2-lookup] .MuiDataGrid-row');
	if (!row || e.target.closest('input, button, a, label, [role="button"], .MuiCheckbox-root')) return;
	const box = row.querySelector('.MuiDataGrid-cell input[type="checkbox"]');
	if (box && !box.checked && !box.disabled) box.click();
}

function onEscape(e) {
	if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) return;
	const t = e.target;
	if (t && t.getAttribute && t.getAttribute('aria-expanded') === 'true') return; // an open autocomplete or select closes first
	const root = topModal();
	if (!root || !root.classList.contains('MuiDialog-root')) return; // menus and popovers close themselves
	const paper = paperOf(root);
	if (!paper || isFullScreen(paper)) return;
	const b = dismissButton(paper);
	if (!b) return;
	e.preventDefault();
	e.stopPropagation();
	b.click();
}

// R11: the same error message isn't stacked twice; it auto-dismisses after the same 5 s as Classic (react-toastify default).
export const ERROR_TOAST_AUTOCLOSE_MS = 5000;
export function stickyErrors(t = toast) {
	const own = t.error;
	if (own.apw2Sticky) return () => {};
	const sticky = (content, options) => own(content, { autoClose: ERROR_TOAST_AUTOCLOSE_MS, ...(typeof content === 'string' ? { toastId: 'apw2:' + content } : {}), ...options });
	sticky.apw2Sticky = true;
	t.error = sticky;
	return () => { t.error = own; };
}

export default function DialogKit() {
	const [hosts, setHosts] = useState([]);
	useEffect(() => stickyErrors(), []);
	useEffect(() => {
		let frame = 0, watched = null;
		const busy = new MutationObserver(() => schedule());
		const run = () => {
			frame = 0;
			const backdrop = document.querySelector(BUSY_SELECTOR); // the busy backdrop's fade only changes its style
			if (backdrop && backdrop !== watched) { busy.disconnect(); busy.observe(backdrop, { attributes: true, attributeFilter: ['style', 'class'] }); watched = backdrop; }
			noteBusy(backdropBusy());
			const errors = [...document.querySelectorAll('.Toastify__toast--error')];
			if (errors.length) noteErrorToast();
			if (errors.some(t => !toastsSeen.has(t))) { errors.forEach(t => toastsSeen.add(t)); markRefusedRow(document); } // R12
			const next = [...document.body.children].filter(r => r.classList && r.classList.contains('MuiDialog-root') && isOpenModal(r)).map(tidy).filter(Boolean);
			setHosts(prev => (prev.length === next.length && prev.every((h, i) => h.host === next[i].host) ? prev : next));
		};
		const schedule = () => { if (!frame) frame = requestAnimationFrame(run); };
		const observer = new MutationObserver(schedule); // page content and popups, anywhere under <body>
		observer.observe(document.body, { childList: true, subtree: true, characterData: true });
		run();
		document.addEventListener('keydown', onEscape, true);
		document.addEventListener('click', onLookupRowClick);
		document.addEventListener('input', clearFixed, true);
		return () => {
			document.removeEventListener('input', clearFixed, true);
			observer.disconnect(); busy.disconnect(); if (frame) cancelAnimationFrame(frame);
			document.removeEventListener('keydown', onEscape, true);
			document.removeEventListener('click', onLookupRowClick);
		};
	}, []);
	return hosts.map(({ host, paper }, i) => createPortal(
		<button type="button" className="apw2-dk-x" aria-label="Close dialog" title="Close (Esc)" onClick={() => { const b = dismissButton(paper); if (b) b.click(); }}>
			<CloseIcon fontSize="small" aria-hidden="true" />
		</button>, host, 'dk' + i));
}
